import type { RouteViewModelControlTask } from '../../../types/routeViewModel';
import type { RouteViewModelDocumentRequirement } from '../../../types/routeViewModel';

export type DocumentReadinessState =
  | 'missing_required'
  | 'optional_missing'
  | 'uploaded_pending_verification'
  | 'verified';

export type FinanceDocumentLine = {
  key: 'invoice' | 'upd' | 'originals';
  label: string;
  state: DocumentReadinessState;
  /** Legacy boolean for chips — true only when verified */
  verified: boolean;
};

const STATE_COLORS: Record<DocumentReadinessState, string> = {
  missing_required: 'error',
  optional_missing: 'default',
  uploaded_pending_verification: 'processing',
  verified: 'success'
};

const STATE_SYMBOLS: Record<DocumentReadinessState, string> = {
  missing_required: '✕',
  optional_missing: '○',
  uploaded_pending_verification: '…',
  verified: '✓'
};

export function documentReadinessTagColor(state: DocumentReadinessState): string {
  return STATE_COLORS[state];
}

export function documentReadinessSymbol(state: DocumentReadinessState): string {
  return STATE_SYMBOLS[state];
}

function findRequirement(
  requirements: RouteViewModelDocumentRequirement[],
  key: RouteViewModelDocumentRequirement['key']
): RouteViewModelDocumentRequirement | undefined {
  return requirements.find((r) => r.key === key);
}

function isTaskOpen(tasks: RouteViewModelControlTask[], taskType: string): boolean {
  return tasks.some(
    (t) =>
      t.taskType === taskType &&
      t.status !== 'COMPLETED' &&
      t.status !== 'DONE' &&
      t.status !== 'CANCELLED'
  );
}

function isTaskDone(tasks: RouteViewModelControlTask[], taskType: string): boolean {
  return tasks.some(
    (t) => t.taskType === taskType && (t.status === 'COMPLETED' || t.status === 'DONE')
  );
}

function mapRequirementToState(
  req: RouteViewModelDocumentRequirement | undefined,
  confirmTaskType: string | null,
  tasks: RouteViewModelControlTask[]
): DocumentReadinessState {
  if (!req) return 'optional_missing';
  if (req.status === 'SATISFIED') {
    if (confirmTaskType) {
      if (isTaskOpen(tasks, confirmTaskType)) return 'uploaded_pending_verification';
      if (isTaskDone(tasks, confirmTaskType)) return 'verified';
      return 'uploaded_pending_verification';
    }
    return 'verified';
  }
  if (req.status === 'OVERDUE') return 'missing_required';
  return 'missing_required';
}

/**
 * Document-aware finance readiness — does NOT treat execution-complete as "green" without files.
 */
export function buildFinanceDocumentLines(params: {
  requirements: RouteViewModelDocumentRequirement[];
  controlTasks: RouteViewModelControlTask[];
}): FinanceDocumentLine[] {
  const { requirements, controlTasks } = params;

  const invoiceReq = findRequirement(requirements, 'CARRIER_INVOICE');
  const updReq = findRequirement(requirements, 'CARGO_DOCS');
  const originalsReq = findRequirement(requirements, 'ORIGINAL_TN');

  const invoiceState = mapRequirementToState(
    invoiceReq,
    'CARRIER_INVOICE_CONFIRM',
    controlTasks
  );
  const updState = mapRequirementToState(updReq, 'UPD_CONFIRM', controlTasks);
  const originalsState = mapRequirementToState(originalsReq, null, controlTasks);

  return [
    {
      key: 'invoice',
      label: 'Счёт',
      state: invoiceState,
      verified: invoiceState === 'verified'
    },
    {
      key: 'upd',
      label: 'УПД',
      state: updState,
      verified: updState === 'verified'
    },
    {
      key: 'originals',
      label: 'Ориг.',
      state: originalsState,
      verified: originalsState === 'verified'
    }
  ];
}

export function financeLinesToReadinessFlags(lines: FinanceDocumentLine[]): {
  invoiceReady: boolean;
  updReady: boolean;
  originalsReady: boolean;
} {
  const byKey = Object.fromEntries(lines.map((l) => [l.key, l])) as Record<
    string,
    FinanceDocumentLine
  >;
  return {
    invoiceReady: byKey.invoice?.verified ?? false,
    updReady: byKey.upd?.verified ?? false,
    originalsReady: byKey.originals?.verified ?? false
  };
}
